import React from 'react';
import { useTranslation } from 'react-i18next';

// Components
import { MainButton } from '../../../inputs/buttons/MainButton';
import ModalContainer from '../../containers/ModalContainer';
import { SpecialTag } from '../../../display/SpecialTag';

// Styled Components
import {
  Container,
  ContainerText,
  Title,
  Text,
  ButtonsContainer,
  ViewOrderLink,
  PendingATButton,
  WideButton,
  StateRow,
} from './styles';

// Types
import { ConfirmCloseOrderModalProps } from './types';
import { variant as tagVariant } from '../../../display/SpecialTag/types';
import { OrderState } from '../../../../types/orders';
import { useTheme } from 'styled-components';

/**
 * T99: Modal de confirmacion para cerrar orden de trabajo.
 * Se muestra cuando todas las actuaciones de una orden han sido validadas.
 * El usuario puede elegir cerrar la orden o mantenerla abierta.
 */
export const ConfirmCloseOrderModal = ({
  onClickClose,
  onConfirmClose,
  onCancelClose,
  onSetPendingAT,
  onConfirmCloseWithActions,
  isLoading = false,
  parentOrderId,
  parentRoid,
  orderType,
  orderDescription,
  orderState,
  routeId,
  ...props
}: ConfirmCloseOrderModalProps): JSX.Element => {
  const { t } = useTranslation(['modals', 'common']);
  const theme = useTheme();

  // Recorregut de Vigilància: mismo modal, gesto distinto. Se decide por el
  // TIPO REAL del padre, no por el prefijo del ROID — el `startsWith('Ca')` de
  // la campaña es un apaño heredado, no un patrón a replicar con 'Su'.
  const isRoute = orderType === 'surveillanceRoute';

  // Quien valida desde Gestión Horaria decide sin abrir la OT: el chip dice en
  // qué estado está AHORA (el hook la lee `network-only` justo antes de abrir
  // el modal). Se acepta solo un valor del enum de estados — un string
  // desconocido pintaría un chip sin texto ni color, así que en ese caso no se
  // pinta nada.
  const stateVariant = (Object.values(OrderState) as string[]).includes(
    orderState ?? ''
  )
    ? (orderState as tagVariant)
    : undefined;
  const isCampaign =
    !isRoute && (orderType === 'campaign' || parentRoid?.startsWith('Ca'));
  const orderUrl = isRoute
    ? `/surveillance-route/${routeId}`
    : isCampaign
      ? `/work-orders/campaign/${parentOrderId}`
      : `/work-orders/${parentOrderId}`;

  return (
    <ModalContainer width={50} fill={theme.colors.black} onClickClose={onClickClose}>
      <Container {...props}>
        <ContainerText>
          <Title variant="semiBold">
            {t('modals:confirmCloseOrder.title', 'Totes les actuacions validades')}
          </Title>
          <Text>
            {isRoute
              ? t(
                  'modals:confirmCloseOrder.messageRoute',
                  "S'han validat totes les actuacions d'aquest recorregut. Vols validar el recorregut?"
                )
              : t(
                  'modals:confirmCloseOrder.message',
                  "S'han validat totes les actuacions d'aquesta ordre de treball. Vols tancar l'ordre?"
                )}
          </Text>
          {(parentRoid || orderDescription) && (
            <Text>{[parentRoid, orderDescription].filter(Boolean).join(' · ')}</Text>
          )}
          {stateVariant && (
            <StateRow>
              <Text variant="medium" size="S">
                {t('common:labels.status')}
              </Text>
              <SpecialTag variant={stateVariant} sizeTag="small" />
            </StateRow>
          )}
        </ContainerText>
        {parentOrderId && (
          <ViewOrderLink
            href={orderUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              e.preventDefault();
              window.open(orderUrl, '_blank');
            }}
          >
            🔗 {isRoute
              ? t('modals:confirmCloseOrder.viewRoute', 'Veure el recorregut')
              : isCampaign
                ? t('pages:campaigns.view')
                : t('modals:confirmCloseOrder.viewOrder', 'Veure ordre de treball')
            }{parentRoid && ` (${parentRoid})`}
          </ViewOrderLink>
        )}
        <ButtonsContainer>
          <MainButton
            variant="secondary"
            text={t('modals:confirmCloseOrder.keepOpen', 'No, mantenir oberta')}
            onClick={onCancelClose}
            disabled={isLoading}
          />
          {onSetPendingAT && (
            <PendingATButton
              variant="tertiary"
              text={t(
                'modals:confirmCloseOrder.pendingAT',
                "Pendent d'A.T (assistència tècnica)"
              )}
              onClick={onSetPendingAT}
              disabled={isLoading}
            />
          )}
          <MainButton
            text={
              isRoute
                ? t(
                    'modals:confirmCloseOrder.validateRoute',
                    'Sí, validar el recorregut'
                  )
                : t('modals:confirmCloseOrder.close', "Si, tancar l'ordre")
            }
            onClick={onConfirmClose}
            disabled={isLoading}
          />
          {onConfirmCloseWithActions && (
            <WideButton
              text={
                isRoute
                  ? t(
                      'modals:confirmCloseOrder.validateRouteWithActions',
                      'Validar el recorregut i tancar les actuacions'
                    )
                  : t(
                      'modals:confirmCloseOrder.closeWithActions',
                      "Tancar l'ordre i les actuacions"
                    )
              }
              onClick={onConfirmCloseWithActions}
              disabled={isLoading}
            />
          )}
        </ButtonsContainer>
      </Container>
    </ModalContainer>
  );
};

export default ConfirmCloseOrderModal;
